import { Controller } from "@hotwired/stimulus"

// Lazy-loads Mermaid.js from CDN and renders flowchart diagrams.
// Usage:
//   <div data-controller="mermaid" data-mermaid-diagram-value="flowchart TD...">
//     <div class="flex justify-center"><%= spinner %></div>
//   </div>
export default class extends Controller {
  static values = {
    diagram: String
  }

  connect() {
    this.loadAndRender()
  }

  async loadAndRender() {
    try {
      await this.loadMermaid()
      await this.renderDiagram()
    } catch (error) {
      console.error('Failed to render Mermaid diagram:', error)
      this.showError()
    }
  }

  async loadMermaid() {
    // Check if mermaid is already loaded
    if (window.mermaid) {
      return
    }

    // Load mermaid from CDN
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js'
      script.onload = () => {
        // Initialize mermaid with our theme settings
        window.mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          themeVariables: {
            // Match Tailwind color palette
            primaryColor: '#7C3AED',
            primaryTextColor: '#fff',
            primaryBorderColor: '#5B21B6',
            lineColor: '#6B7280',
            secondaryColor: '#F3F4F6',
            tertiaryColor: '#fff',
            // Font - smaller size to match normal text
            fontFamily: 'Neue Haas Grotesk Text Pro, system-ui, sans-serif',
            fontSize: '12px'
          },
          flowchart: {
            htmlLabels: true,
            curve: 'basis',
            padding: 8,
            nodeSpacing: 30,
            rankSpacing: 40
          }
        })
        resolve()
      }
      script.onerror = () => reject(new Error('Failed to load Mermaid'))
      document.head.appendChild(script)
    })
  }

  async renderDiagram() {
    if (!this.diagramValue) {
      this.showError('No diagram data provided')
      return
    }

    // Generate a unique ID for this diagram
    const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    try {
      const { svg } = await window.mermaid.render(id, this.diagramValue)
      this.element.innerHTML = svg

      // Set fixed height on the SVG to prevent vertical scrolling
      const svgElement = this.element.querySelector('svg')
      if (svgElement) {
        svgElement.style.height = 'auto'
        svgElement.style.minWidth = 'fit-content'
      }
    } catch (error) {
      console.error('Mermaid render error:', error)
      this.showError('Failed to render diagram')
    }
  }

  showError(message = 'Failed to load workflow diagram') {
    this.element.innerHTML = `
      <div class="text-center py-8 text-support-dark-500">
        <div class="mb-2">${message}</div>
        <button
          class="text-sm text-core-purple-600 hover:underline"
          onclick="this.closest('[data-controller=mermaid]').dispatchEvent(new CustomEvent('retry'))"
        >
          Try again
        </button>
      </div>
    `
  }

  // Allow retry via custom event
  retry() {
    this.element.innerHTML = `
      <div class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-core-purple-600"></div>
      </div>
    `
    this.loadAndRender()
  }
}
